'use client';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import { Feature4 } from '@/components/sections/feature4';
import {
  BubbleAttachment,
  BubbleEmailDraft,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

const CONTENT = {
  en: {
    eyebrow: 'Channels',
    title: 'Inbox zero, on autopilot',
    subtitleA: 'Your customers get answers in minutes — not days.',
    subtitleB: 'Your team gets out of the queue and back to real work.',
    insight1: 'Total $245 · paid May 12',
    body1: 'Hi Sarah — your order shipped 2h ago and arrives tomorrow.',
    insight2: 'Order #4633 · refund eligible',
    body2: 'Refund of $89 processed — back on your card in 3–5 days.',
    sentLabel: 'Sent',
  },
  ar: {
    eyebrow: 'القنوات',
    title: 'صندوق الوارد يفضى بنفسه',
    subtitleA: 'عميلك ياخذ رده بدقائق — لا بأيام.',
    subtitleB: 'وفريقك يطلع من الطابور ويرجع للشغل الحقيقي.',
    // ⁦…⁩ = LRI/PDI. Without the isolates RTL bidi renders "$245" as
    // "245$" and "#4633" as "4633#".
    insight1: 'الإجمالي ⁦$245⁩ · مدفوع ١٢ مايو',
    body1: 'أهلاً سارة — طلبك انشحن قبل ساعتين ويوصلك بكرة.',
    insight2: 'طلب ⁦#4633⁩ · قابل للاسترجاع',
    body2: 'رجّعنا لك ⁦$89⁩ — توصل لبطاقتك خلال ٣–٥ أيام.',
    sentLabel: 'تم الإرسال',
  },
} as const;

export default function EmailPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={
          <>
            {c.subtitleA}
            <br />
            {c.subtitleB}
          </>
        }
        videoSrc="/home/features/email.mp4"
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleAttachment
                fileName="invoice-may-2024.pdf"
                fileType="PDF"
                insight={c.insight1}
              />
            ),
          },
          {
            role: 'agent',
            widget: <BubbleEmailDraft to="sarah@example.com" body={c.body1} />,
          },
          {
            role: 'agent',
            widget: (
              <BubbleAttachment
                fileName="receipt-screenshot.png"
                fileType="IMG"
                insight={c.insight2}
              />
            ),
          },
          {
            role: 'agent',
            widget: (
              <BubbleEmailDraft
                to="marcus@example.com"
                body={c.body2}
                primaryLabel={c.sentLabel}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.channels['Email AI Features']}
        subtitle={t.channels['Turn your inbox into a competitive advantage']}
        learnMoreLink="#faq"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.channels['Your Brand, Not Ours'],
            description:
              t.channels[
                "AI responds from your domain. Customers never know it's AI—just perfect, instant support from your company."
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-1.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Reads Attachments Like a Human'],
            description:
              t.channels[
                'Screenshots, PDFs, invoices—Agent 5 sees and understands them. Then references them in replies.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-2.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Respond in Minutes, Not Days'],
            description:
              t.channels[
                'While competitors keep customers waiting 24+ hours, you respond in minutes. Every time.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Invisible AI-to-Human Handoffs'],
            description:
              t.channels[
                'Same thread. No forwarding. No "transferring you". Customer never notices the switch.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-4.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Every Past Email Remembered'],
            description:
              t.channels[
                'Agent 5 recalls entire email history. Customers never repeat themselves.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-5.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['One Dashboard, All Your Inboxes'],
            description:
              t.channels[
                'Support@, sales@, billing@—monitor everything. Smart routing to the right team, automatically.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-1.svg',
            imagePosition: 'left',
          },
        ]}
      />

      <Feature4
        heading={
          t.channels[
            'Email AI that works with your domain and existing email infrastructure'
          ]
        }
      />

      {/* FAQ Section */}
      <FaqSection
        title={t.channels['Frequently asked questions']}
        subtitle={
          t.channels['Find answers to common questions about Email support.']
        }
        items={[
          {
            question: t.channels['Can I use my own email domain?'],
            answer:
              t.channels[
                'Yes. Agent 5 responds from your domain (like support@yourcompany.com). We handle all DNS setup including SPF, DKIM, and DMARC.'
              ],
          },
          {
            question: t.channels['Can Agent 5 read attachments?'],
            answer:
              t.channels[
                'Yes. Agent 5 can read PDFs, images, documents, and other attachments, then reference them in responses to provide accurate help.'
              ],
          },
          {
            question: t.channels['How do email handoffs to human agents work?'],
            answer:
              t.channels[
                'The same email thread continues. When a human takes over, they reply in the same conversation with full context. The customer never knows a handoff happened.'
              ],
          },
          {
            question:
              t.channels['Can I have multiple support email addresses?'],
            answer:
              t.channels[
                'Yes. Monitor sales@, support@, billing@, and any other addresses. Route them to different teams or workflows from one dashboard.'
              ],
          },
          {
            question: t.channels['What are temporary email addresses for?'],
            answer:
              t.channels[
                'Use them for testing, specific campaigns, or time-limited initiatives. Create disposable addresses that expire when you no longer need them.'
              ],
          },
          {
            question: t.channels['How fast does Agent 5 respond to emails?'],
            answer:
              t.channels[
                'Agent 5 responds within seconds of receiving an email. Your customers get instant replies instead of waiting hours or days.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
